import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MCQSection from './components/MCQSection';
import MockInterviewSection from './components/MockInterviewSection';
import ProgressAnalytics from './components/ProgressAnalytics';
import CompanySpecificPrep from './components/CompanySpecificPrep';
import PeerPracticeSection from './components/PeerPracticeSection';
import InterviewPrepPublicPreview from './components/InterviewPrepPublicPreview';

const InterviewPreparationSuite = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('mcq');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Changed back to true for proper auth check
  const [userProgress, setUserProgress] = useState({
    mcqCompleted: 0,
    mockInterviews: 0,
    readinessScore: 0,
    weakAreas: []
  });
  const [isMobile, setIsMobile] = useState(false);

  // Proper authentication check - removed auto mock user assignment
  useEffect(() => {
    const checkAuthentication = () => {
      try {
        const authToken = localStorage.getItem('authToken');
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        const userData = localStorage.getItem('userData');
        
        if (authToken && userData) {
          // Validate session expiry if remember me is not enabled
          const loginTime = localStorage.getItem('loginTime');
          const currentTime = Date.now();
          const sessionDuration = 24 * 60 * 60 * 1000; // 24 hours
          
          if (rememberMe || (loginTime && (currentTime - parseInt(loginTime)) < sessionDuration)) {
            // Valid session, restore user
            try {
              const parsedUser = JSON.parse(userData);
              setUser(parsedUser);
              // Load authenticated user progress
              setUserProgress({
                mcqCompleted: 45,
                mockInterviews: 8,
                readinessScore: 82,
                weakAreas: ['Performance Testing', 'API Security']
              });
            } catch (error) {
              console.error('Error parsing user data:', error);
              // Clear invalid data
              localStorage.removeItem('authToken');
              localStorage.removeItem('userData');
              localStorage.removeItem('rememberMe');
              localStorage.removeItem('loginTime');
              setUser(null);
            }
          } else {
            // Session expired, clear data
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('loginTime');
            setUser(null);
          }
        } else {
          // No authentication, user remains null
          setUser(null);
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  // Check authentication and load user-specific data
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      // Mock authenticated user
      setUser({
        id: 1,
        username: "alex_developer",
        email: "alex@example.com",
        level: 3,
        progress: 65
      });
      // Load user-specific interview preparation progress
      setUserProgress({
        mcqCompleted: 45,
        mockInterviews: 8,
        readinessScore: 82,
        weakAreas: ['Performance Testing', 'API Security']
      });
    } else {
      // Set default progress for non-authenticated users
      setUserProgress({
        mcqCompleted: 0,
        mockInterviews: 0,
        readinessScore: 0,
        weakAreas: ['Take assessments to identify weak areas']
      });
    }
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const tabs = [
    { id: 'mcq', label: 'MCQ Practice', icon: '📋' },
    { id: 'mock', label: 'Mock Interviews', icon: '🎥' },
    { id: 'analytics', label: 'Progress Analytics', icon: '📊' },
    { id: 'companies', label: 'Company Prep', icon: '🏢' },
    { id: 'peer', label: 'Peer Practice', icon: '👥' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'mcq':
        return <MCQSection userProgress={userProgress} isAuthenticated={!!user} />;
      case 'mock':
        return <MockInterviewSection userProgress={userProgress} isAuthenticated={!!user} />;
      case 'analytics':
        return <ProgressAnalytics userProgress={userProgress} isAuthenticated={!!user} />;
      case 'companies':
        return <CompanySpecificPrep isAuthenticated={!!user} />;
      case 'peer':
        return <PeerPracticeSection isAuthenticated={!!user} />;
      default:
        return <MCQSection userProgress={userProgress} isAuthenticated={!!user} />;
    }
  };

  const handleLogout = () => {
    // Clear all authentication tokens and user data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('loginTime');
    
    // Clear user state
    setUser(null);
    
    // Navigate to home page
    navigate('/');
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Interview Preparation Suite...</p>
        </div>
      </div>
    );
  }

  // Show public preview for non-authenticated users
  if (!user) {
    return (
      <>
        <Helmet>
          <title>Interview Preparation Suite - Testing Academy</title>
          <meta 
            name="description" 
            content="Master testing interviews with comprehensive MCQ practice, mock interviews, and company-specific preparation materials" 
          />
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <Header user={user} onLogout={handleLogout} />
          
          {/* Public Hero Section */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16 pt-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
                  Interview Preparation Suite
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-blue-100">
                  Master testing interviews with comprehensive practice and AI-powered feedback
                </p>
                <p className="text-lg text-blue-200 max-w-3xl mx-auto">
                  From Manual Testing fundamentals to advanced Automation frameworks, API testing expertise, 
                  and testing tool mastery - prepare for interviews at top tech companies with confidence.
                </p>
              </div>
            </div>
          </section>

          {/* Public Preview Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <InterviewPrepPublicPreview />
          </div>
        </div>
      </>
    );
  }

  // Show authenticated interface for logged-in users
  return (
    <>
      <Helmet>
        <title>Interview Preparation Suite - Testing Academy</title>
        <meta 
          name="description" 
          content="Comprehensive testing interview preparation with MCQ assessments, mock interviews, and company-specific resources" 
        />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Header user={user} onLogout={handleLogout} />
        
        {/* Authenticated Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16 pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
                Interview Preparation Suite
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Welcome back {user?.username}! Master testing interviews with personalized practice and AI-powered feedback.
              </p>
              
              {/* Quick Stats with user-specific data */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                <div className="bg-white/25 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-white">{userProgress?.mcqCompleted || 0}</div>
                  <div className="text-sm text-white font-medium">MCQs Completed</div>
                </div>
                <div className="bg-white/25 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-white">{userProgress?.mockInterviews || 0}</div>
                  <div className="text-sm text-white font-medium">Mock Interviews</div>
                </div>
                <div className="bg-white/25 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-white">{userProgress?.readinessScore || 0}%</div>
                  <div className="text-sm text-white font-medium">Readiness Score</div>
                </div>
                <div className="bg-white/25 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-white">12</div>
                  <div className="text-sm text-white font-medium">Companies</div>
                </div>
              </div>

              {/* Show progress insights for authenticated users */}
              {userProgress?.weakAreas?.length > 0 && (
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mt-6 max-w-2xl mx-auto">
                  <h3 className="text-lg font-semibold text-white mb-2">Areas to Focus On</h3>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {userProgress?.weakAreas?.map((area, index) => (
                      <span 
                        key={index}
                        className="bg-white/30 text-white px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Main Content with enhanced background */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation Tabs with improved contrast */}
          <div className={`${isMobile ? 'overflow-x-auto' : ''} mb-8`}>
            <div className={`flex ${isMobile ? 'min-w-max space-x-2' : 'justify-center space-x-4'}`}>
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 border-2
                    ${activeTab === tab?.id 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-lg transform scale-105' 
                      : 'bg-white text-gray-800 border-gray-300 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-400 shadow-md'
                    }
                    ${isMobile ? 'text-sm px-4 py-2' : ''}
                  `}
                >
                  <span className="text-lg">{tab?.icon}</span>
                  <span className={`font-semibold ${isMobile ? 'text-xs' : ''}`}>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content with enhanced styling */}
          <div className="min-h-[600px] bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-100">
            {renderTabContent()}
          </div>
        </div>

        {/* Floating Action Button (Mobile) - Enhanced colors */}
        {isMobile && (
          <div className="fixed bottom-6 right-6 z-50">
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-white/20">
              <span className="text-xl">🚀</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default InterviewPreparationSuite;